import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  Progress,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toasterApi } from '@ui';
import React from 'react';
import {
  LuCheck,
  LuDownload,
  LuExternalLink,
  LuFileImage,
  LuPrinter,
  LuRotateCw,
  LuX,
} from 'react-icons/lu';
import { TrackingApi } from '@/state/api';
import { TrackingType } from '@/types';
import {
  KARAS_RED,
  newRequestId,
  openPdfBlob,
  printPdfBlob,
  saveBlob,
} from '../utils';

const statusColor: Record<TrackingType.JobStatus, string> = {
  PREPARING: 'gray',
  QUEUED: 'gray',
  GENERATING: 'blue',
  READY: 'green',
  FAILED: 'red',
  CANCELLED: 'gray',
};

const formatWhen = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

export const JobPanel: React.FC<{
  jobId: string;
  namesByCode?: Record<string, string>;
}> = ({ jobId, namesByCode }) => {
  const [polling, setPolling] = React.useState(1500);
  const { data: job } = TrackingApi.Api.useGetJobQuery(
    { id: jobId },
    { pollingInterval: polling },
  );
  const [getOutput, { isLoading: fetching }] =
    TrackingApi.Api.useGetJobOutputMutation();
  const [action, { isLoading: acting }] =
    TrackingApi.Api.usePostJobActionMutation();
  const [pdf, setPdf] = React.useState<Blob | null>(null);
  const [mobileReady, setMobileReady] = React.useState(false);
  const [showItems, setShowItems] = React.useState(false);

  React.useEffect(() => {
    if (job && ['READY', 'FAILED', 'CANCELLED'].includes(job.status))
      setPolling(0);
    else setPolling(1500);
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (job?.status !== 'READY' || pdf) return;
    getOutput({ id: jobId })
      .unwrap()
      .then(setPdf)
      .catch(() => undefined);
  }, [job?.status, jobId, pdf, getOutput]);

  if (!job) return <Text fontSize="sm">Loading print job…</Text>;
  const name =
    job.job_type === 'REPORTS' ? 'Karas-item-reports' : 'Karas-labels';
  const lines = job.items.map((item) => ({
    title: namesByCode?.[item.code] || item.display_code,
    code: item.display_code,
    copies: item.copies,
  }));
  const title =
    lines.length === 0
      ? job.job_type === 'REPORTS'
        ? 'Reports'
        : 'Labels'
      : lines.length === 1
        ? lines[0].title
        : `${lines[0].title} and ${lines.length - 1} more`;
  const detail =
    lines.length === 1
      ? lines[0].copies > 1
        ? `${lines[0].code} · ${lines[0].copies} copies`
        : lines[0].code
      : `${lines.length} items`;
  const counts =
    job.job_type === 'REPORTS'
      ? `${job.item_count} report${job.item_count === 1 ? '' : 's'}`
      : `${job.label_count} label${job.label_count === 1 ? '' : 's'}`;

  const onPrint = async () => {
    if (!pdf) return;
    await action({
      id: jobId,
      action: 'print-request',
      request_id: newRequestId(),
    })
      .unwrap()
      .catch(() => undefined);
    if (!printPdfBlob(pdf)) setMobileReady(true);
  };

  const onDownload = async (kind: 'pdf' | 'svg' | 'png') => {
    try {
      const blob =
        kind === 'pdf' && pdf
          ? pdf
          : await getOutput({ id: jobId, kind, index: 0 }).unwrap();
      saveBlob(blob, `${name}-${jobId.slice(0, 8)}.${kind}`);
    } catch {
      toasterApi({
        type: 'error',
        status: 500,
        code: 'Output',
        message: 'The file could not be downloaded',
      });
    }
  };

  return (
    <Stack
      gap={0}
      borderWidth="1px"
      borderColor="border.muted"
      borderRadius="lg"
      bg="white"
      w="100%"
      minW={0}
      overflow="hidden"
      aria-live="polite"
    >
      <Stack gap={3} px={{ base: 3, md: 4 }} py={{ base: 3, md: 4 }}>
        <Flex justify="space-between" align="flex-start" gap={4}>
          <Stack gap={1} minW={0} flex="1">
            <Text fontSize="md" fontWeight="semibold" lineHeight="short" wordBreak="break-word">
              {title}
            </Text>
            <Text fontSize="sm" color="fg.muted" lineHeight="short" wordBreak="break-word">
              {detail}
            </Text>
          </Stack>
          <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap" lineHeight="short" pt="1px">
            {formatWhen(job.created_at)}
          </Text>
        </Flex>
        {lines.length > 1 && (
          <Box>
            <Button
              size="sm"
              variant="ghost"
              px={0}
              onClick={() => setShowItems((open) => !open)}
            >
              {showItems ? 'Hide items' : 'Show all items'}
            </Button>
            {showItems && (
              <Stack gap={1} pt={1}>
                {lines.map((line) => (
                  <Text key={line.code} fontSize="sm" wordBreak="break-word">
                    {line.title}
                    <Text as="span" color="fg.muted">
                      {' '}
                      · {line.code}
                      {line.copies > 1 ? ` · ${line.copies} copies` : ''}
                    </Text>
                  </Text>
                ))}
              </Stack>
            )}
          </Box>
        )}
        <HStack gap={2} flexWrap="wrap" align="center">
          <Badge colorPalette={statusColor[job.status]} size="md">
            {job.status_label}
          </Badge>
          <Text fontSize="sm" lineHeight="short">
            {counts}
            {job.page_count
              ? ` · ${job.page_count} page${job.page_count === 1 ? '' : 's'}`
              : ''}
          </Text>
          {job.print_requested_count > 0 && (
            <Text fontSize="sm" color="fg.muted" lineHeight="short">
              Print dialog opened {job.print_requested_count}{' '}
              {job.print_requested_count === 1 ? 'time' : 'times'}.
            </Text>
          )}
        </HStack>
      {(job.status === 'QUEUED' ||
        job.status === 'GENERATING' ||
        job.status === 'PREPARING') && (
        <>
          <Progress.Root value={null} size="xs">
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <Text fontSize="sm" color="fg.muted">
            Generating. You can close this window; the job continues and appears
            under Jobs.
          </Text>
          <Box>
            <Button
              size="sm"
              variant="outline"
              onClick={() => action({ id: jobId, action: 'cancel' })}
              loading={acting}
            >
              <LuX /> Cancel
            </Button>
          </Box>
        </>
      )}
      {job.status === 'FAILED' && (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Box>
            <Alert.Title>
              {job.error?.message ?? 'Generation failed.'}
            </Alert.Title>
            <Button
              mt={2}
              size="sm"
              onClick={() => action({ id: jobId, action: 'retry' })}
              loading={acting}
            >
              <LuRotateCw /> Retry
            </Button>
          </Box>
        </Alert.Root>
      )}
      {job.status === 'CANCELLED' && (
        <Text fontSize="sm" color="fg.muted">
          This job was cancelled.
        </Text>
      )}
      </Stack>
      {job.status === 'READY' && (
        <Flex
          borderTopWidth="1px"
          borderColor="border.muted"
          bg="gray.50"
          px={{ base: 3, md: 4 }}
          py={3}
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={3}
        >
          <HStack gap={2} flexWrap="wrap" align="center">
            <Button
              size="sm"
              bg={KARAS_RED}
              color="white"
              _hover={{ bg: '#732f2f' }}
              onClick={onPrint}
              disabled={!pdf}
              loading={fetching && !pdf}
            >
              <LuPrinter /> Print
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownload('pdf')}
              disabled={!pdf}
            >
              <LuDownload /> Download PDF
            </Button>
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button size="sm" variant="outline">
                  More
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    value="open"
                    disabled={!pdf}
                    onClick={() => pdf && openPdfBlob(pdf)}
                  >
                    <LuExternalLink /> Open PDF
                  </Menu.Item>
                  {job.job_type === 'LABELS' && (
                    <>
                      <Menu.Item value="svg" onClick={() => onDownload('svg')}>
                        <LuFileImage /> Download SVG
                      </Menu.Item>
                      <Menu.Item value="png" onClick={() => onDownload('png')}>
                        <LuFileImage /> Download PNG
                      </Menu.Item>
                    </>
                  )}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </HStack>
          <Stack gap={2} align={{ base: 'flex-start', md: 'flex-end' }}>
            {mobileReady && pdf && (
              <HStack gap={2} align="center">
                <Text fontSize="sm">
                  Open the PDF, then print it from the viewer.
                </Text>
                <Button
                  size="sm"
                  onClick={() => {
                    openPdfBlob(pdf);
                    setMobileReady(false);
                  }}
                >
                  Open PDF
                </Button>
              </HStack>
            )}
            {job.user_confirmed_at ? (
              <HStack gap={2} color="fg" align="center">
                <LuCheck />
                <Text fontSize="sm">
                  Confirmed by user · {formatWhen(job.user_confirmed_at)}
                </Text>
              </HStack>
            ) : (
              <HStack gap={3} flexWrap="wrap" align="center">
                <Text fontSize="sm" lineHeight="short">
                  Print not confirmed.
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    action({
                      id: jobId,
                      action: 'confirm',
                      request_id: newRequestId(),
                    })
                  }
                  loading={acting}
                >
                  <LuCheck /> Confirm printed
                </Button>
              </HStack>
            )}
          </Stack>
        </Flex>
      )}
    </Stack>
  );
};
